// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { ErrorCause, Feedback, ReconciliationItem } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface FormEditReconciliationItemValues {
	name: string;
}

export interface FormEditReconciliationItemProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

export const FormEditReconciliationItem = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormEditReconciliationItemProps) => {
	// hooks
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();

	// context
	const { setBanner } = useContext(ContextBanner);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// params
	const reconciliationItemId = searchParams.get('dialogReconciliationItem');

	// state
	const [values, setValues] = useState<FormEditReconciliationItemValues>({
		name: '',
	});
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [reconciliationItem, setReconciliationItem] = useState<ReconciliationItem | null>(null);

	// vars
	const idForm = 'form-edit-reconciliation-item';

	// fetch reconciliation item
	const { isLoading: isLoadingReconciliationItem } = useFetch({
		isEnabled: !!reconciliationItemId,
		url: `${process.env.REACT_APP_API_URL}/reconciliation-items/${reconciliationItemId}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setReconciliationItem(res.data[0]);
			}
		},
	});

	// set initial values
	useEffect(() => {
		if (reconciliationItem) {
			setValues({
				name: reconciliationItem.name,
			});
		}
	}, [reconciliationItem]);

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// set loader
			if (setIsLoading) {
				setIsLoading(true);
			}

			const fetchEditReconciliationItem = await fetch(
				`${process.env.REACT_APP_API_URL}/reconciliation-items/${reconciliationItemId}`,
				{
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						'Content-Type': 'application/json',
						User: String(idToken),
					},
					body: JSON.stringify({
						name: values.name,
					}),
				}
			);
			const resEditReconciliationItem = await fetchEditReconciliationItem.json();

			if (resEditReconciliationItem.error) {
				throw new Error(resEditReconciliationItem.error.message, {
					cause: {
						id: resEditReconciliationItem.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.reconciliationItemUpdated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'RECONCILIATION_ITEM_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				} else if (cause.id === 'RECONCILIATION_ITEM_DEFAULT_CANNOT_EDIT') {
					feedbackNew.alert.children = t('feedback.reconciliationItemDefaultCannotEdit');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loader
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	if (isLoadingReconciliationItem) {
		return <Loading />;
	}

	return (
		<form id={idForm} name="formEditReconciliationItem" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
